import { Link } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import googleAdsLogo from '/google-ads.svg';
import metaLogo from '/meta.png';

// A campaign, wherever a row has to name one (#6743). One pill, so the same
// campaign looks the same on a location row, a competitor row and anywhere
// this lands next.
//
// Rules the component encodes, which tables must not undo:
// - The platform's logo leads, because a campaign belongs to Google or Meta
//   and the reader should not have to open it to find out.
// - The name truncates against the column and the whole name is the tooltip.
//   Campaign names are the full four-part Google name and will not fit.
// - Violet, not cobalt: a campaign a row is already in is a state worth
//   seeing, not the page's one committing action.
// - It is a link. A pill that opens nothing is not this component: use
//   CAMPAIGN_PILL_SHAPE to wear the same shape in another colour.

/**
 * The pill's metrics on their own: every pill in a Campaign column is this
 * tall and this size, whether it is this link, a grey state, or the trigger
 * of a dropdown, so a column never steps up and down the rows.
 */
export const PILL_METRICS = 'rounded-full px-2.5 py-0.5 text-xs font-medium';

/** The shape alone, for a pill that is not a link (a campaign still being built). */
export const CAMPAIGN_PILL_SHAPE = cn('inline-block max-w-full truncate align-middle', PILL_METRICS);

const PILL = cn(
  CAMPAIGN_PILL_SHAPE,
  'bg-[#EDE9FE] text-[#290BD9] transition-colors hover:bg-[#DDD6FE]',
);

const LOGO = 'mr-1 inline-block h-3 w-auto align-[-0.15em]';

interface CampaignPillProps {
  /** The campaign's slug: what its URL is addressed by. */
  slug: string;
  /** What the pill says, already shortened by the caller if it shortens it. */
  name: string;
  platform?: 'google' | 'meta';
  /** The campaign tab to open, e.g. "targeting". Omitted opens the campaign. */
  hash?: string;
  /** Overrides the tooltip, which is otherwise the full name. */
  title?: string;
  /** A pill inside a clickable row stops the row opening too. */
  onClick?: (e: MouseEvent) => void;
  className?: string;
}

export function CampaignPill({
  slug,
  name,
  platform = 'google',
  hash,
  title,
  onClick,
  className,
}: CampaignPillProps): ReactNode {
  const logo =
    platform === 'meta' ? (
      <img src={metaLogo} alt="Meta" className={LOGO} />
    ) : (
      <img src={googleAdsLogo} alt="Google Ads" className={LOGO} />
    );
  const label = title ?? name;
  return platform === 'meta' ? (
    <Link
      to="/campaigns/meta/$metaCampaignSlug"
      params={{ metaCampaignSlug: slug }}
      hash={hash}
      onClick={onClick}
      title={label}
      className={cn(PILL, className)}
    >
      {logo}
      {name}
    </Link>
  ) : (
    <Link
      to="/campaigns/google/$campaignslug"
      params={{ campaignslug: slug }}
      hash={hash}
      onClick={onClick}
      title={label}
      className={cn(PILL, className)}
    >
      {logo}
      {name}
    </Link>
  );
}
